import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLanguage } from '../../i18n/useLanguage';
import './ImageCropper.css';

const PREVIEW_SIZE = 280;   // circle diameter in px
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const OUTPUT_SIZE = 400;    // output square in px

interface Props {
  file: File;
  onConfirm: (croppedFile: File) => void;
  onCancel: () => void;
}

/**
 * Modal image cropper.
 * Shows the selected image inside a circular viewport.
 * The user can drag to reposition and scroll / pinch / use the slider to zoom.
 * On "Apply" the visible square area is exported as a JPEG and returned via onConfirm.
 */
export const ImageCropper: React.FC<Props> = ({ file, onConfirm, onCancel }) => {
  const { t } = useLanguage();

  // ── image source ──────────────────────────────────────────────────────────
  const [imageSrc, setImageSrc] = useState('');

  // ── mutable state held in refs (event handlers read latest values without
  //    needing to be re-registered on every render)
  const zoomRef   = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const natRef    = useRef({ w: 0, h: 0 });
  const dragRef   = useRef(false);
  const lastPos   = useRef({ x: 0, y: 0 });
  const pinchRef  = useRef<{ dist: number; startZoom: number } | null>(null);

  // ── React state used only for rendering ───────────────────────────────────
  const [zoom,    setZoomR]   = useState(1);
  const [offset,  setOffsetR] = useState({ x: 0, y: 0 });
  const [natSize, setNatSize] = useState({ w: 0, h: 0 });

  const containerRef = useRef<HTMLDivElement>(null);

  // Sync helpers: update both ref and render-state in one call
  const applyZoom = useCallback((z: number) => {
    zoomRef.current = z;
    setZoomR(z);
  }, []);
  const applyOffset = useCallback((o: { x: number; y: number }) => {
    offsetRef.current = o;
    setOffsetR(o);
  }, []);

  // ── clamp offset so image always covers the circle ─────────────────────────
  const clamp = useCallback((ox: number, oy: number, z: number) => {
    const { w, h } = natRef.current;
    if (!w || !h) return { x: 0, y: 0 };
    const cs   = Math.max(PREVIEW_SIZE / w, PREVIEW_SIZE / h);
    const maxX = Math.max(0, (w * cs * z - PREVIEW_SIZE) / 2);
    const maxY = Math.max(0, (h * cs * z - PREVIEW_SIZE) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, ox)),
      y: Math.max(-maxY, Math.min(maxY, oy)),
    };
  }, []); // stable – reads refs, not state

  // ── load image ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    zoomRef.current   = 1;
    offsetRef.current = { x: 0, y: 0 };
    setZoomR(1);
    setOffsetR({ x: 0, y: 0 });

    const img = new Image();
    img.onload = () => {
      natRef.current = { w: img.naturalWidth, h: img.naturalHeight };
      setNatSize({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // ── wheel zoom (non-passive so we can preventDefault) ─────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const newZ = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoomRef.current - e.deltaY * 0.005));
      const newO = clamp(offsetRef.current.x, offsetRef.current.y, newZ);
      applyZoom(newZ);
      applyOffset(newO);
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [clamp, applyZoom, applyOffset]);

  // ── touch drag + pinch-to-zoom (non-passive to prevent page scroll) ────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 2) {
        dragRef.current = false;
        const dx = e.touches[1].clientX - e.touches[0].clientX;
        const dy = e.touches[1].clientY - e.touches[0].clientY;
        pinchRef.current = {
          dist: Math.sqrt(dx * dx + dy * dy),
          startZoom: zoomRef.current,
        };
      } else {
        pinchRef.current = null;
        dragRef.current  = true;
        lastPos.current  = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 2 && pinchRef.current) {
        const dx   = e.touches[1].clientX - e.touches[0].clientX;
        const dy   = e.touches[1].clientY - e.touches[0].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const newZ = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM,
          pinchRef.current.startZoom * (dist / pinchRef.current.dist)));
        const newO = clamp(offsetRef.current.x, offsetRef.current.y, newZ);
        applyZoom(newZ);
        applyOffset(newO);
      } else if (e.touches.length === 1 && dragRef.current) {
        const dx = e.touches[0].clientX - lastPos.current.x;
        const dy = e.touches[0].clientY - lastPos.current.y;
        lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        applyOffset(clamp(offsetRef.current.x + dx, offsetRef.current.y + dy, zoomRef.current));
      }
    };

    const onTouchEnd = () => {
      dragRef.current  = false;
      pinchRef.current = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove',  onTouchMove,  { passive: false });
    el.addEventListener('touchend',   onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   onTouchEnd);
    };
  }, [clamp, applyZoom, applyOffset]);

  // ── mouse drag (document-level so dragging outside the circle still works) ─
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - lastPos.current.x;
      const dy = e.clientY - lastPos.current.y;
      lastPos.current = { x: e.clientX, y: e.clientY };
      applyOffset(clamp(offsetRef.current.x + dx, offsetRef.current.y + dy, zoomRef.current));
    };
    const onUp = () => { dragRef.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
  }, [clamp, applyOffset]);

  // ── export the cropped image ───────────────────────────────────────────────
  const handleConfirm = useCallback(async () => {
    if (!imageSrc || !natRef.current.w) return;

    const canvas = document.createElement('canvas');
    canvas.width  = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.src = imageSrc;
    await new Promise<void>(res => { img.onload = () => res(); });

    const { w, h } = natRef.current;
    const ratio     = OUTPUT_SIZE / PREVIEW_SIZE;
    const cs        = Math.max(PREVIEW_SIZE / w, PREVIEW_SIZE / h);
    const s         = cs * zoomRef.current * ratio;

    // White background (JPEG has no transparency)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    ctx.drawImage(
      img,
      (OUTPUT_SIZE - w * s) / 2 + offsetRef.current.x * ratio,
      (OUTPUT_SIZE - h * s) / 2 + offsetRef.current.y * ratio,
      w * s,
      h * s,
    );

    canvas.toBlob(blob => {
      if (!blob) return;
      const name = file.name.replace(/\.[^.]*$/, '') + '.jpg';
      onConfirm(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }));
    }, 'image/jpeg', 0.92);
  }, [imageSrc, file, onConfirm]);

  // ── computed display geometry ──────────────────────────────────────────────
  const cs   = natSize.w && natSize.h
    ? Math.max(PREVIEW_SIZE / natSize.w, PREVIEW_SIZE / natSize.h)
    : 1;
  const dispW = natSize.w * cs * zoom;
  const dispH = natSize.h * cs * zoom;
  const imgL  = (PREVIEW_SIZE - dispW) / 2 + offset.x;
  const imgT  = (PREVIEW_SIZE - dispH) / 2 + offset.y;

  return (
    <div className="ic-overlay" onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="ic-modal">
        <h3 className="ic-title">{t('cropper.title')}</h3>

        {/* ── circular viewport ── */}
        <div className="ic-stage">
          <div
            ref={containerRef}
            className="ic-circle"
            onMouseDown={e => {
              e.preventDefault();
              dragRef.current = true;
              lastPos.current = { x: e.clientX, y: e.clientY };
            }}
          >
            {imageSrc ? (
              <img
                src={imageSrc}
                alt=""
                style={{
                  position: 'absolute',
                  left:   imgL,
                  top:    imgT,
                  width:  dispW,
                  height: dispH,
                  userSelect:    'none',
                  pointerEvents: 'none',
                  draggable:     false,
                } as React.CSSProperties}
              />
            ) : (
              <div className="ic-placeholder" />
            )}
          </div>
        </div>

        {/* ── zoom slider ── */}
        <div className="ic-zoom-row">
          <button
            className="ic-zoom-btn"
            onClick={() => {
              const newZ = Math.max(MIN_ZOOM, zoomRef.current - 0.2);
              applyZoom(newZ);
              applyOffset(clamp(offsetRef.current.x, offsetRef.current.y, newZ));
            }}
            aria-label="zoom out"
          >−</button>
          <input
            type="range"
            className="ic-zoom-slider"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={e => {
              const newZ = parseFloat(e.target.value);
              const newO = clamp(offsetRef.current.x, offsetRef.current.y, newZ);
              applyZoom(newZ);
              applyOffset(newO);
            }}
          />
          <button
            className="ic-zoom-btn"
            onClick={() => {
              const newZ = Math.min(MAX_ZOOM, zoomRef.current + 0.2);
              applyZoom(newZ);
              applyOffset(clamp(offsetRef.current.x, offsetRef.current.y, newZ));
            }}
            aria-label="zoom in"
          >+</button>
        </div>

        <p className="ic-hint">{t('cropper.hint')}</p>

        {/* ── action buttons ── */}
        <div className="ic-actions">
          <button className="button button--outline" onClick={onCancel}>
            {t('cropper.cancel')}
          </button>
          <button className="button button--primary" onClick={handleConfirm}>
            {t('cropper.apply')}
          </button>
        </div>
      </div>
    </div>
  );
};
